import type React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type ContactStatus = "未対応" | "対応中" | "解決済み" | "保留中";

// BadgeのVariant型を修正
type BadgeVariant = "default" | "destructive" | "outline" | "secondary";

interface Contact {
	id: string;
	name: string;
	email: string;
	category: string;
	subject: string;
	status: ContactStatus;
	createdAt: string;
	updatedAt: string;
}

const ContactManagementPage: React.FC = () => {
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState<ContactStatus | "all">(
		"all",
	);

	// 仮のデータ（実際はAPIから取得）
	const contacts: Contact[] = [
		{
			id: "1",
			name: "山田太郎",
			email: "yamada@example.com",
			category: "一般的な問い合わせ",
			subject: "もくもく会について",
			status: "未対応",
			createdAt: "2024-03-20T10:00:00",
			updatedAt: "2024-03-20T10:00:00",
		},
		// 他のお問い合わせデータ
	];

	const getStatusBadgeVariant = (status: ContactStatus): BadgeVariant => {
		const variants: Record<ContactStatus, BadgeVariant> = {
			未対応: "destructive",
			対応中: "default",
			解決済み: "secondary",
			保留中: "outline",
		};
		return variants[status];
	};

	const filteredContacts = contacts.filter((contact) => {
		const matchesSearch =
			contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
			contact.subject.toLowerCase().includes(searchQuery.toLowerCase());

		const matchesStatus =
			statusFilter === "all" || contact.status === statusFilter;

		return matchesSearch && matchesStatus;
	});

	return (
		<div className="space-y-6">
			<h1 className="text-3xl font-bold">お問い合わせ管理</h1>

			<div className="flex justify-between items-center gap-4">
				<div className="flex-1 max-w-sm space-y-2">
					<Label htmlFor="search">検索</Label>
					<Input
						id="search"
						placeholder="検索..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="status">ステータス</Label>
					<Select
						value={statusFilter}
						onValueChange={(value) =>
							setStatusFilter(value as ContactStatus | "all")
						}
					>
						<SelectTrigger id="status" className="w-[180px]">
							<SelectValue placeholder="ステータスで絞り込み" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">すべて</SelectItem>
							<SelectItem value="未対応">未対応</SelectItem>
							<SelectItem value="対応中">対応中</SelectItem>
							<SelectItem value="解決済み">解決済み</SelectItem>
							<SelectItem value="保留中">保留中</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>日時</TableHead>
						<TableHead>名前</TableHead>
						<TableHead>メールアドレス</TableHead>
						<TableHead>カテゴリ</TableHead>
						<TableHead>件名</TableHead>
						<TableHead>ステータス</TableHead>
						<TableHead>アクション</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{filteredContacts.map((contact) => (
						<TableRow key={contact.id}>
							<TableCell>
								{new Date(contact.createdAt).toLocaleString()}
							</TableCell>
							<TableCell>{contact.name}</TableCell>
							<TableCell>{contact.email}</TableCell>
							<TableCell>{contact.category}</TableCell>
							<TableCell>{contact.subject}</TableCell>
							<TableCell>
								<Badge variant={getStatusBadgeVariant(contact.status)}>
									{contact.status}
								</Badge>
							</TableCell>
							<TableCell>
								<Link to={`/webapp/admin/contacts/${contact.id}`}>
									<Button variant="outline" size="sm">
										詳細
									</Button>
								</Link>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};

export default ContactManagementPage;
